import { FormEvent } from "react"
import { useForm } from "@inertiajs/react"
import { useIntl } from "react-intl"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Radio } from "@/components/ui/radio"
import { Select } from "@/components/ui/select"
import { CHOICE_LETTERS } from "./format"
import type { ContentAreaGroup } from "@/types/flashcards"
import type { Question } from "@/types/quizzes"

type QuestionFormProps = {
  idPrefix: string
  action: string
  method: "post" | "patch"
  contentAreaGroups: ContentAreaGroup[]
  question?: Question
  requireExplanation: boolean
  submitLabel: string
  onSuccess?: () => void
  onCancel?: () => void
  /** Case-study questions also pick the section they follow. */
  sections?: { value: string; label: string }[]
  defaultSection?: string
}

export function QuestionForm({
  idPrefix,
  action,
  method,
  contentAreaGroups,
  question,
  requireExplanation,
  submitLabel,
  onSuccess,
  onCancel,
  sections,
  defaultSection,
}: QuestionFormProps) {
  const intl = useIntl()
  const form = useForm({
    text: question?.text ?? "",
    choices: question?.choices ?? ["", "", "", ""],
    correct_choice: question ? String(question.correct_choice) : "",
    explanation: question?.explanation ?? "",
    content_area_id: question?.content_area_id ? String(question.content_area_id) : "",
    // Only case-study questions use a section; quiz controllers ignore it.
    section: question?.section ?? defaultSection ?? sections?.[0]?.value ?? "",
  })

  const submit = (e: FormEvent) => {
    e.preventDefault()
    form.submit(method, action, {
      preserveScroll: true,
      onSuccess: () => {
        if (method === "post") form.reset("text", "choices", "correct_choice", "explanation")
        onSuccess?.()
      },
    })
  }

  const setChoice = (index: number, value: string) =>
    form.setData(
      "choices",
      form.data.choices.map((choice, i) => (i === index ? value : choice)),
    )

  const hasAreas = contentAreaGroups.some((group) => group.areas.length > 0)
  const choiceError = form.errors.choices || form.errors.correct_choice

  return (
    <form onSubmit={submit} className="space-y-4">
      {sections && (
        <div className="space-y-2">
          <label htmlFor={`${idPrefix}-section`}>{intl.formatMessage({ id: "quizzes.follows" })}</label>
          <Select
            id={`${idPrefix}-section`}
            value={form.data.section}
            onChange={(e) => form.setData("section", e.target.value)}
          >
            {sections.map((section) => (
              <option key={section.value} value={section.value}>
                {section.label}
              </option>
            ))}
          </Select>
        </div>
      )}
      <div className="space-y-2">
        <label htmlFor={`${idPrefix}-text`}>{intl.formatMessage({ id: "quizzes.question" })}</label>
        <textarea
          id={`${idPrefix}-text`}
          className="form-control form-control-textarea"
          required
          aria-invalid={!!form.errors.text}
          value={form.data.text}
          onChange={(e) => form.setData("text", e.target.value)}
        />
        {form.errors.text && <p className="text-xs text-danger-display">{form.errors.text}</p>}
      </div>

      <fieldset className="space-y-2">
        <legend>{intl.formatMessage({ id: "quizzes.answer_choices" })}</legend>
        <p className="text-xs text-ink-muted">{intl.formatMessage({ id: "quizzes.select_correct" })}</p>
        {CHOICE_LETTERS.map((letter, index) => (
          <div key={letter} className="flex items-center gap-3">
            <Radio
              id={`${idPrefix}-correct-${index}`}
              name={`${idPrefix}-correct`}
              value={index}
              required
              aria-label={intl.formatMessage({ id: "quizzes.choice_correct" }, { letter })}
              checked={form.data.correct_choice === String(index)}
              onChange={() => form.setData("correct_choice", String(index))}
            />
            <span className="w-4 text-sm font-medium text-ink-muted" aria-hidden>
              {letter}
            </span>
            <Input
              id={`${idPrefix}-choice-${index}`}
              aria-label={intl.formatMessage({ id: "quizzes.choice_n" }, { letter })}
              required
              aria-invalid={!!form.errors.choices}
              value={form.data.choices[index]}
              onChange={(e) => setChoice(index, e.target.value)}
            />
          </div>
        ))}
        {choiceError && <p className="text-xs text-danger-display">{choiceError}</p>}
      </fieldset>

      <div className="space-y-2">
        <label htmlFor={`${idPrefix}-explanation`}>
          {requireExplanation ? intl.formatMessage({ id: "quizzes.explanation" }) : intl.formatMessage({ id: "quizzes.explanation_optional" })}
        </label>
        <textarea
          id={`${idPrefix}-explanation`}
          className="form-control form-control-textarea"
          required={requireExplanation}
          placeholder={intl.formatMessage({ id: "quizzes.explanation_placeholder" })}
          aria-invalid={!!form.errors.explanation}
          value={form.data.explanation}
          onChange={(e) => form.setData("explanation", e.target.value)}
        />
        {form.errors.explanation && <p className="text-xs text-danger-display">{form.errors.explanation}</p>}
      </div>

      {hasAreas && (
        <div className="space-y-2">
          <label htmlFor={`${idPrefix}-area`}>{intl.formatMessage({ id: "quizzes.content_area" })}</label>
          <Select
            id={`${idPrefix}-area`}
            value={form.data.content_area_id}
            onChange={(e) => form.setData("content_area_id", e.target.value)}
          >
            <option value="">{intl.formatMessage({ id: "quizzes.none" })}</option>
            {contentAreaGroups.length === 1
              ? contentAreaOptions(contentAreaGroups[0])
              : contentAreaGroups.map((group) => (
                  <optgroup key={group.label} label={group.label}>
                    {contentAreaOptions(group)}
                  </optgroup>
                ))}
          </Select>
        </div>
      )}

      <div className="flex items-center gap-2">
        <Button type="submit" disabled={form.processing}>
          {submitLabel}
        </Button>
        {onCancel && (
          <Button type="button" variant="secondary" onClick={onCancel}>
            {intl.formatMessage({ id: "common.cancel" })}
          </Button>
        )}
      </div>
    </form>
  )
}

function contentAreaOptions(group: ContentAreaGroup) {
  return group.areas.map((area) => (
    <option key={area.id} value={area.id}>
      {area.name}
    </option>
  ))
}
